import {SeatConfig, SeatSlot, SeatSlotWithPosition} from '../types';
import {normalizeAngleDeg} from './fabric-factory';

export type CirclePreset = 'full' | 'top-half' | 'right-half' | 'bottom-half' | 'left-half';

export const CIRCLE_PRESET_LAYOUTS: Record<
    CirclePreset,
    {startAngleDeg: number; sweepAngleDeg: number}
> = {
    full: {startAngleDeg: 0, sweepAngleDeg: 360},
    'top-half': {startAngleDeg: 270, sweepAngleDeg: 180},
    'right-half': {startAngleDeg: 0, sweepAngleDeg: 180},
    'bottom-half': {startAngleDeg: 90, sweepAngleDeg: 180},
    'left-half': {startAngleDeg: 180, sweepAngleDeg: 180},
};

// Read a seat's effective ordering even when priority was never explicitly stored.
export const effectivePriority = (slot: {
    priority?: number | null;
    position?: number | null;
}) => {
    const priority = slot.priority ?? slot.position ?? 0;
    return Number.isFinite(priority) ? (priority as number) : 0;
};

// Build the seat numbers shown in the UI from booking order, not raw slot position.
export const buildDisplayNumberById = (visualSlots: SeatSlotWithPosition[]) => {
    const sorted = [...visualSlots].sort((left, right) => {
        const leftPriority = effectivePriority(left);
        const rightPriority = effectivePriority(right);

        if (leftPriority !== rightPriority) return leftPriority - rightPriority;
        return (left.position ?? 0) - (right.position ?? 0);
    });

    const result = new Map<string, number>();
    sorted.forEach((slot, index) => result.set(slot.id, index + 1));
    return result;
};

// Preserve the current order as a fallback when swapping seats with missing stored priorities.
export const buildFallbackPriorityById = (visualSlots: SeatSlotWithPosition[]) => {
    const result = new Map<string, number>();
    visualSlots.forEach((slot) => result.set(slot.id, effectivePriority(slot)));
    return result;
};

// Re-number circle seats from the configured arc start so seat order matches the visual layout.
export const normalizeCircleSlotPriorities = (
    slots: SeatSlot[],
    circle?: SeatConfig['circle']
): SeatSlot[] => {
    const startAngleDeg = normalizeAngleDeg(circle?.startAngleDeg ?? 0);
    const sorted = [...slots].sort((left, right) => {
        const leftDistance = normalizeAngleDeg((left.angleDeg ?? 0) - startAngleDeg);
        const rightDistance = normalizeAngleDeg((right.angleDeg ?? 0) - startAngleDeg);

        if (leftDistance !== rightDistance) return leftDistance - rightDistance;
        return effectivePriority(left) - effectivePriority(right);
    });

    const priorityById = new Map<string, number>();
    sorted.forEach((slot, index) => priorityById.set(slot.id, index));

    return slots.map((slot) => ({
        ...slot,
        priority: priorityById.get(slot.id) ?? slot.priority ?? slot.position,
    }));
};

// Round normalized angles before comparing them to fixed circle presets.
export const roundedAngle = (angleDeg: number) => Math.round(normalizeAngleDeg(angleDeg));

// Convert a circle angle into an SVG point around the table preview.
export const getCirclePoint = (
    angleDeg: number,
    radius: number,
    seatOffset: number
) => {
    const radians = (angleDeg * Math.PI) / 180;
    return {
        x: (radius + seatOffset) * Math.sin(radians),
        y: -(radius + seatOffset) * Math.cos(radians),
    };
};

// Match the current circle layout to a preset so the select shows a stable label.
export const getCirclePresetValue = (circle: {
    startAngleDeg: number;
    sweepAngleDeg: number;
}): CirclePreset | '' => {
    const currentStart = roundedAngle(circle.startAngleDeg);
    const currentSweep = Math.round(circle.sweepAngleDeg);

    const preset = (
        Object.entries(CIRCLE_PRESET_LAYOUTS) as Array<
            [CirclePreset, {startAngleDeg: number; sweepAngleDeg: number}]
        >
    ).find(([, value]) => (
        roundedAngle(value.startAngleDeg) === currentStart &&
        value.sweepAngleDeg === currentSweep
    ));

    return preset?.[0] ?? '';
};

// Draw the dashed arc guide that shows where circle seats can be placed.
export const buildCircleArcPath = (
    centerX: number,
    centerY: number,
    radius: number,
    startAngleDeg: number,
    sweepAngleDeg: number
) => {
    const start = getCirclePoint(startAngleDeg, radius, 0);
    const end = getCirclePoint(startAngleDeg + sweepAngleDeg, radius, 0);
    const largeArcFlag = sweepAngleDeg > 180 ? 1 : 0;

    return `M ${centerX + start.x} ${centerY + start.y} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${centerX + end.x} ${centerY + end.y}`;
};

const angularDistanceDeg = (fromDeg: number, toDeg: number) => {
    const clockwise = normalizeAngleDeg(toDeg - fromDeg);
    const counterClockwise = normalizeAngleDeg(fromDeg - toDeg);
    return Math.min(clockwise, counterClockwise);
};

// Keep dragged circle seats inside the configured arc while preserving free movement on full circles.
export const clampAngleToCircleArc = (
    angleDeg: number,
    startAngleDeg: number,
    sweepAngleDeg: number
) => {
    const normalizedAngle = normalizeAngleDeg(angleDeg);
    const normalizedStart = normalizeAngleDeg(startAngleDeg);
    const normalizedSweep = Math.max(0, Math.min(360, sweepAngleDeg));

    if (normalizedSweep >= 360) return normalizedAngle;
    if (normalizedSweep <= 0) return normalizedStart;

    const distanceFromStart = normalizeAngleDeg(normalizedAngle - normalizedStart);
    if (distanceFromStart <= normalizedSweep) return normalizedAngle;

    const endAngleDeg = normalizeAngleDeg(normalizedStart + normalizedSweep);
    return angularDistanceDeg(normalizedAngle, normalizedStart) <= angularDistanceDeg(normalizedAngle, endAngleDeg)
        ? normalizedStart
        : endAngleDeg;
};
